import { useEffect, useMemo, useState } from "react";
import {
  CrmLead, CLASSIFICATIONS, WON_STAGES, brl, fetchEvents, logEvent, stageLabel, updateLead,
} from "@/lib/crm";
import { CrmAiInsight, fetchInsights, insertRow } from "@/lib/erp";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Brain, Copy, Loader2, MessageSquare, Sparkles, TrendingUp, Wand2 } from "lucide-react";

type Mode = "qualificacao" | "atendimento" | "mensagem_automatica" | "conversao";

const MODES: { key: Mode; label: string; icon: React.ReactNode; hint: string }[] = [
  { key: "qualificacao", label: "Qualificação por IA", icon: <Brain className="h-4 w-4" />, hint: "Pontuação, classificação e potencial de compra" },
  { key: "atendimento", label: "Sugestões de atendimento", icon: <Sparkles className="h-4 w-4" />, hint: "Abordagem, argumentos e objeções" },
  { key: "mensagem_automatica", label: "Mensagens automáticas", icon: <MessageSquare className="h-4 w-4" />, hint: "WhatsApp, follow up e e-mail prontos" },
  { key: "conversao", label: "Análise de conversão", icon: <TrendingUp className="h-4 w-4" />, hint: "Gargalos do funil e ações da semana" },
];

interface Props {
  leads: CrmLead[];
  onOpenLead: (lead: CrmLead) => void;
  onRefresh: () => Promise<void> | void;
}

const CrmIntelligence = ({ leads, onOpenLead, onRefresh }: Props) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("qualificacao");
  const [leadId, setLeadId] = useState<string>("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(false);
  const [insights, setInsights] = useState<CrmAiInsight[]>([]);

  const loadInsights = async () => setInsights(await fetchInsights());
  useEffect(() => {
    void loadInsights();
  }, []);

  const lead = useMemo(() => leads.find((l) => l.id === leadId) || null, [leads, leadId]);

  const metrics = useMemo(() => {
    const total = leads.length;
    const won = leads.filter((l) => WON_STAGES.includes(l.stage)).length;
    const lost = leads.filter((l) => l.stage === "perdido").length;
    const byStage: Record<string, number> = {};
    const bySource: Record<string, number> = {};
    leads.forEach((l) => {
      byStage[stageLabel(l.stage)] = (byStage[stageLabel(l.stage)] || 0) + 1;
      const s = l.source || "Não informado";
      bySource[s] = (bySource[s] || 0) + 1;
    });
    const valorGanho = leads
      .filter((l) => WON_STAGES.includes(l.stage))
      .reduce((a, l) => a + Number(l.deal_value || 0), 0);
    return {
      total_leads: total,
      negocios_ganhos: won,
      negocios_perdidos: lost,
      taxa_conversao_pct: total ? Number(((won / total) * 100).toFixed(1)) : 0,
      valor_total_ganho: valorGanho,
      leads_por_etapa: byStage,
      leads_por_origem: bySource,
    };
  }, [leads]);

  const callAI = async (target: CrmLead | null, useMode: Mode) => {
    const events = target ? await fetchEvents(target.id) : [];
    const { data, error } = await supabase.functions.invoke("crm-intelligence", {
      body: {
        mode: useMode,
        lead: target
          ? {
              nome: target.client_name, telefone: target.client_phone, email: target.client_email,
              etapa: stageLabel(target.stage), classificacao: target.classification, score: target.score,
              origem: target.source, campanha: target.campaign, objetivo: target.goal,
              tipo_cliente: target.client_type, faixa_preco: target.price_range, regioes: target.regions,
              interesses: target.interests, quartos: target.bedrooms, vaga: target.garage,
              renda: target.income, profissao: target.profession, cidade: target.city,
              imovel_interesse: target.property_name, bairro: target.neighborhood,
              valor_negocio: target.deal_value, ultimo_contato: target.last_contact_at,
              observacoes: target.general_notes, criado_em: target.created_at,
            }
          : undefined,
        events: events.slice(0, 25).map((e) => ({ tipo: e.event_type, titulo: e.title, quando: e.created_at })),
        metrics: useMode === "conversao" ? metrics : undefined,
        leads:
          useMode === "conversao"
            ? leads.slice(0, 60).map((l) => ({
                etapa: stageLabel(l.stage), classificacao: l.classification, origem: l.source,
                faixa: l.price_range, valor: l.deal_value, criado_em: l.created_at,
              }))
            : undefined,
      },
    });
    if (error) throw error;
    return data as { text: string; score: number | null; classification: string | null; nextSteps: string | null };
  };

  const run = async () => {
    if (mode !== "conversao" && !lead) {
      return toast({ title: "Escolha um lead", variant: "destructive" });
    }
    setLoading(true);
    setOutput("");
    try {
      const res = await callAI(mode === "conversao" ? null : lead, mode);
      setOutput(res.text || "A IA não retornou conteúdo.");
      if (lead) {
        await insertRow("crm_ai_insights", {
          lead_id: lead.id,
          insight_type: mode,
          summary: (res.text || "").slice(0, 4000),
          suggested_score: res.score,
          suggested_classification: res.classification,
          next_steps: res.nextSteps,
        });
        if (mode === "qualificacao" && res.score !== null) {
          await updateLead(lead.id, {
            score: res.score,
            classification: res.classification || lead.classification,
          });
          await logEvent(lead.id, "nota", `IA qualificou o lead: ${res.score}/100 (${res.classification || "—"})`);
          await onRefresh();
        }
        await loadInsights();
      }
    } catch (e) {
      toast({ title: "Erro na IA", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const runBatch = async () => {
    const targets = leads
      .filter((l) => l.stage !== "perdido" && !WON_STAGES.includes(l.stage))
      .slice(0, 15);
    if (!targets.length) return toast({ title: "Nenhum lead ativo para qualificar" });
    setBatch(true);
    let ok = 0;
    for (const t of targets) {
      try {
        const res = await callAI(t, "qualificacao");
        await insertRow("crm_ai_insights", {
          lead_id: t.id,
          insight_type: "qualificacao",
          summary: (res.text || "").slice(0, 4000),
          suggested_score: res.score,
          suggested_classification: res.classification,
          next_steps: res.nextSteps,
        });
        if (res.score !== null) {
          await updateLead(t.id, {
            score: res.score,
            classification: res.classification || t.classification,
          });
          ok += 1;
        }
      } catch {
        /* segue para o próximo lead */
      }
    }
    setBatch(false);
    await onRefresh();
    await loadInsights();
    toast({ title: `${ok} lead(s) qualificados pela IA` });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => {
              setMode(m.key);
              setOutput("");
            }}
            className={`text-left p-4 rounded-lg border transition-colors ${
              mode === m.key ? "border-accent bg-accent/10" : "border-border bg-card hover:border-accent/50"
            }`}
          >
            <span className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              {m.icon}
              {m.label}
            </span>
            <span className="mt-1 block font-body text-xs text-muted-foreground">{m.hint}</span>
          </button>
        ))}
      </div>

      <Card className="p-4 border border-border space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {mode !== "conversao" && (
            <Select value={leadId} onValueChange={setLeadId}>
              <SelectTrigger className="font-body w-full sm:w-80 h-9">
                <SelectValue placeholder="Selecione o lead" />
              </SelectTrigger>
              <SelectContent>
                {leads.map((l) => (
                  <SelectItem key={l.id} value={l.id} className="font-body">
                    {l.client_name} — {stageLabel(l.stage)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="gold" onClick={run} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Gerar com IA
          </Button>
          {mode === "qualificacao" && (
            <Button size="sm" variant="outline" onClick={runBatch} disabled={batch}>
              {batch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
              Qualificar leads ativos em lote
            </Button>
          )}
          {lead && (
            <Button size="sm" variant="secondary" onClick={() => onOpenLead(lead)}>
              Abrir ficha
            </Button>
          )}
        </div>

        {mode === "conversao" && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Leads", String(metrics.total_leads)],
              ["Ganhos", String(metrics.negocios_ganhos)],
              ["Perdidos", String(metrics.negocios_perdidos)],
              ["Conversão", `${metrics.taxa_conversao_pct}%`],
              ["Valor ganho", brl(metrics.valor_total_ganho)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md border border-border p-3">
                <p className="font-body text-[11px] text-muted-foreground">{k}</p>
                <p className="font-display text-lg font-bold text-foreground">{v}</p>
              </div>
            ))}
          </div>
        )}

        {output && (
          <div className="space-y-2">
            <Textarea value={output} onChange={(e) => setOutput(e.target.value)} rows={16} className="font-body text-sm" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(output);
                toast({ title: "Copiado" });
              }}
            >
              <Copy className="h-4 w-4" /> Copiar
            </Button>
          </div>
        )}
      </Card>

      <div>
        <h3 className="font-display text-sm font-bold text-foreground mb-2">Histórico de análises da IA</h3>
        {insights.length === 0 ? (
          <p className="font-body text-xs text-muted-foreground">Nenhuma análise gerada ainda.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {insights.slice(0, 20).map((i) => {
              const l = leads.find((x) => x.id === i.lead_id);
              const cls = i.suggested_classification ? CLASSIFICATIONS[i.suggested_classification] : null;
              return (
                <Card key={i.id} className="p-4 border border-border">
                  <div className="flex items-center gap-2">
                    <p className="font-display text-sm font-bold text-foreground truncate">
                      {l?.client_name || "Lead removido"}
                    </p>
                    <Badge variant="outline" className="font-body text-[10px] ml-auto">
                      {MODES.find((m) => m.key === i.insight_type)?.label || i.insight_type}
                    </Badge>
                    {i.suggested_score !== null && (
                      <Badge variant="outline" className={`font-body text-[10px] ${cls?.className || ""}`}>
                        {i.suggested_score}/100
                      </Badge>
                    )}
                  </div>
                  <p className="mt-2 font-body text-xs text-muted-foreground whitespace-pre-line line-clamp-6">
                    {i.summary}
                  </p>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrmIntelligence;
